import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  Output,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Option } from '@app/forms/formly/formly-utils';

import { GoogleFlightsFeedbackOptionEnum, PortalLeadNode } from '@app/services/api/api.types';
import { GoogleFlightOption } from '@app/services/google-flights/google-flights.types';
import { Tracking } from '@app/services/tracking/tracking.service';
import { PortalTrackingEvents } from '@app/utils/tracking-events';
import { StateService } from '@app/services/state/state.service';

@Component({
  selector: 'travel-expert-info-quick-form',
  templateUrl: './travel-expert-info-quick-form.component.html',
  styleUrl: './travel-expert-info-quick-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TravelExpertInfoQuickFormComponent {
  stateService = inject(StateService);
  hostElement = inject<ElementRef<HTMLElement>>(ElementRef);

  form: FormGroup = new FormGroup({
    budgetFrom: new FormControl<number | null>(null),
    budgetTo: new FormControl<number | null>(null),
    comment: new FormControl(''),
  });

  loading: boolean;
  feedbackOption: Option;

  options: Option[] = [
    {
      label: 'I think this fare option is reasonable',
      value: GoogleFlightsFeedbackOptionEnum.REASONABLE,
    },
    {
      label: 'This fare is way too expensive',
      value: GoogleFlightsFeedbackOptionEnum.TOO_EXPENSIVE,
    },
    {
      label: 'I like the price, but want to see more convenient options',
      value: GoogleFlightsFeedbackOptionEnum.WANT_CONVENIENCE,
    },
    {
      label: 'It’s slightly above my budget',
      value: GoogleFlightsFeedbackOptionEnum.ABOVE_BUDGET,
      extra: { withRange: true },
    },
  ];

  @Input() portalLead: PortalLeadNode;
  @Input() flightOption: GoogleFlightOption;

  @Output() submitQuickquote = new EventEmitter();

  selectFeedbackOption(feedbackOption: Option) {
    if (this.loading) return;

    this.feedbackOption = feedbackOption;

    Tracking.track(PortalTrackingEvents.quickQuoteAnswerClicked, {
      answer: this.feedbackOption.value,
    });

    if (feedbackOption.extra?.['withRange']) {
      setTimeout(() => this.bindCurrencyDataIds());
    }
  }

  bindCurrencyDataIds() {
    const hosts = this.hostElement.nativeElement.querySelectorAll('ds-currency-input[data-id]');

    hosts.forEach((host) => {
      const dataId = host.getAttribute('data-id');
      if (!dataId) {
        return;
      }

      host.querySelector('input')?.setAttribute('data-id', dataId);
      host.removeAttribute('data-id');
    });
  }

  submit() {
    this.loading = true;

    this.form.disable();

    const routeDetails = [
      {
        origin: this.flightOption.outbound.departure_airport.code,
        destination: this.flightOption.outbound.arrival_airport.code,
        date: this.portalLead.firstTrip?.departureAt,
        airline: this.flightOption.outbound.segments.map((s) => s.airline).join(', '),
      },
      ...(this.portalLead.roundTripDepartureAt && this.flightOption.inbound
        ? [
            {
              origin: this.flightOption.inbound.departure_airport.code,
              destination: this.flightOption.inbound.arrival_airport.code,
              date: this.portalLead.roundTripDepartureAt,
              airline: this.flightOption.inbound.segments.map((s) => s.airline).join(', '),
            },
          ]
        : []),
    ];

    const input = {
      feedbackOption: this.feedbackOption.value,
      comment: this.form.get('comment')?.value as string,
      leadPortalLink: this.stateService.getState().portalLink,
      googlePrice: this.flightOption.price,
      displayPrice: this.flightOption.display_price,
      markdownPercentage: this.flightOption.markdown_percentage,
      routeDetails,
    };

    const budgetFrom = Number(this.form.get('budgetFrom')?.value);
    const budgetTo = Number(this.form.get('budgetTo')?.value);

    if (budgetFrom) {
      Object.assign(input, { budgetFrom });
    }

    if (budgetTo) {
      Object.assign(input, { budgetTo });
    }

    this.submitQuickquote.emit(input);

    Tracking.track(PortalTrackingEvents.quickQuoteButtonPressed, input);
  }
}
